import { useLocation, useNavigate } from '@tanstack/react-router';

import { MultipleOfferAvailability } from '../../../../types/MultipleOfferAvailability';
import { Button } from '../../../cdl/Button/Button';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconPlus } from '../../../common/icons/cdl/Plus';
import { OfferModel } from '../../../offer/model/OfferModel';
import { OfferState } from '../../../offer/model/OfferState';
import { getOpenRfqOffers } from '../helpers/getOpenRfqOffers';

import { AdditionalOfferActionButton } from './AdditionalOfferActionButton';

interface RfqStatusBarActionProps {
    offers: OfferModel[];
    rfqId: string;
    rfqState: string;
    multipleOfferAvailability: MultipleOfferAvailability;
}

export const RfqStatusBarAction = ({ offers, rfqId, rfqState, multipleOfferAvailability }: RfqStatusBarActionProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const role = useRole();
    const openOffers = getOpenRfqOffers(offers);
    const hasQuotedOffer = openOffers.some((offer) => offer.wasQuoted());
    const canCreateAdditionalOffer = multipleOfferAvailability === MultipleOfferAvailability.ENABLED;
    const isDisabledForCustomer = multipleOfferAvailability === MultipleOfferAvailability.DISABLED_FOR_CUSTOMER;

    if (!role.isSupplier()) {
        return null;
    }

    if (rfqState === OfferState.ENQUIRY) {
        if (openOffers.length > 1 && hasQuotedOffer) {
            return null;
        }

        if (openOffers.length === 1 && openOffers[0].wasQuoted()) {
            return (
                <Button
                    emphasis="high"
                    onClick={() =>
                        navigate({
                            to: '/offer/$id/quote',
                            params: { id: openOffers[0].id },
                            state: { originLocation: location },
                        })
                    }
                >
                    {translate('statusbar.supplier.updateQuote.button')}
                </Button>
            );
        }

        if (openOffers.length === 1) {
            return (
                <Button
                    LeadingIcon={IconPlus}
                    emphasis="high"
                    onClick={() =>
                        navigate({
                            to: '/offer/$id/quote',
                            params: { id: openOffers[0].id },
                            state: { originLocation: location },
                        })
                    }
                >
                    {translate('statusbar.supplier.enquiry.button')}
                </Button>
            );
        }
    }

    if (
        rfqState === OfferState.QUOTED &&
        openOffers.length > 0 &&
        (canCreateAdditionalOffer || isDisabledForCustomer)
    ) {
        return (
            <AdditionalOfferActionButton
                disabledReason={
                    isDisabledForCustomer ? translate('rfq.detail.additionalOfferUnavailableForCustomer') : undefined
                }
                onClick={() => {
                    navigate({
                        to: '/requests-for-quotes/$id/quote',
                        params: { id: rfqId },
                    });
                }}
            />
        );
    }

    if (rfqState === OfferState.ORDER) {
        const orderedOffer = offers.find((offer) => offer.state === OfferState.ORDER);

        if (!orderedOffer) {
            return null;
        }

        return (
            <Button
                emphasis="high"
                onClick={() => {
                    navigate({
                        to: '/offer/$id',
                        params: { id: orderedOffer.id },
                    });
                }}
            >
                {translate('rfq.detail.showOrder')}
            </Button>
        );
    }

    return null;
};
